import React, { useMemo } from 'react';
import {
  Button,
  FormControl,
  makeStyles,
  createStyles,
  Theme,
  IconButton,
} from '@material-ui/core';
import * as Yup from 'yup';
import { FormBodyAPI } from 'src/constants/fieldsTypes';
import {
  MultiCategorySelect,
  MultiCategorySelectProps,
} from 'src/legacy/components/Select/MultiCategorySelect';
import { RootState } from 'src/store';
import { FlagsContext } from 'src/context';
import { BlackHeadings, GraySmall, HoverBackground } from 'src/theme/colors';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { useCustomDomains } from 'src/hooks/useCustomDomains';
import CopyToClipboard from 'src/legacy/components/CopyToClipboard';
import { CopyIcon } from 'src/legacy/components/Icons';
import RowDivider from 'src/legacy/components/RowDivider';
import { UrlUtils } from 'src/utils';
import {
  useGetFormByIdQuery,
  useGetResponsesByFormIdQuery,
} from 'src/services/api/formsApi';
import { Callout } from 'src/legacy/components/Callout';
import {
  FormResponseStatus,
  FormVisibility,
} from 'src/legacy/components/FormsV2/formsTypes';
import { useAppSelector } from 'src/hooks/useStore';
import { RecipientSelector } from 'src/features/recipient-selector/RecipientSelector';

const validationScheme = Yup.object().shape({
  fields: Yup.object().shape({
    recipientIds: Yup.array()
      .of(
        Yup.object().shape({
          clientId: Yup.string(),
          companyId: Yup.string(),
        }),
      )
      .test(
        'clients-array',
        'Please select at least one client.',
        (value) => value.length > 0,
      ),
  }),
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    copyText: {
      wordBreak: 'break-all',
      color: BlackHeadings,
    },
    iconButton: {
      padding: 'unset',
    },
    copyIcon: {
      fontSize: 12,
    },
    clientLinkContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: HoverBackground,
      padding: theme.spacing(1.5),
    },
    linkTitle: {
      color: GraySmall,
      marginBottom: theme.spacing(1),
    },
    dividerTitle: {
      color: GraySmall,
    },
    callout: {
      marginBottom: theme.spacing(2),
      '& svg': {
        marginTop: theme.spacing(0.5),
      },
    },
  }),
);

export const useShareForm = (
  selectedFormId?: string,
  selectedFormRef?: string,
) => {
  const classes = useStyles();
  const { EnableManyCompanies } = React.useContext(FlagsContext);
  const { basePortalDomain } = useCustomDomains();
  const { data: selectedForm, isLoading: isLoadingForm } = useGetFormByIdQuery(
    selectedFormId as string,
  );
  const { data: formResponses = [], isLoading: isLoadingResponses } =
    useGetResponsesByFormIdQuery(selectedFormId as string);

  const clientUsers = useAppSelector(
    (state: RootState) => state.clients.activeClients,
  );

  const shareFormLink = UrlUtils.GetFullUrl(
    `${basePortalDomain}/sl/form/${selectedFormRef}`,
  );

  const FormRenderer: React.FC<FormBodyAPI> = ({
    errors,
    values,
    setFieldValue,
  }) => {
    const handleRecipientChange: MultiCategorySelectProps['onChange'] = (
      _,
      vals,
    ) => {
      if (!Array.isArray(vals)) return;

      const recipientIds = vals.map((val) => ({
        clientId: val.id,
        companyId: val.companyId,
      }));
      setFieldValue('fields.recipientIds', recipientIds);
    };
    const disableUserIds = useMemo(() => {
      const pendingResponses = formResponses.filter((response) => {
        const status = response?.fields?.status;
        return status === FormResponseStatus.Pending;
      });
      // disable form from being shared with users who are still pending submission
      return pendingResponses?.map((u) => u.clientId);
    }, [selectedForm, formResponses]);

    const selectAllClients = () => {
      const clientsWithFormNotShared = clientUsers
        .filter((c) => !disableUserIds.includes(c.id))
        .map((u) => {
          return {
            clientId: u.id,
            companyId: u.fields.companyId,
          };
        });
      setFieldValue('fields.recipientIds', clientsWithFormNotShared);
    };

    const selectedClientIds = useMemo(() => {
      return (
        values.fields.recipientIds?.map(
          (r: { clientId: string; companyId: string }) => r.clientId,
        ) || []
      );
    }, [values.fields.recipientIds]);

    return (
      <>
        {selectedForm?.fields?.visibility ===
          FormVisibility.RequestedClients && (
          <Callout className={classes.callout}>
            The form link is only accessible to clients who have been sent the
            form.
          </Callout>
        )}
        <BaseTypography fontType="13Regular" className={classes.linkTitle}>
          Share form link with your client
        </BaseTypography>
        <div className={classes.clientLinkContainer}>
          <BaseTypography className={classes.copyText} fontType="12Regular">
            {shareFormLink}
          </BaseTypography>
          <CopyToClipboard>
            {({ copy }) => (
              <IconButton
                className={classes.iconButton}
                onClick={() => copy(shareFormLink)}
              >
                <CopyIcon className={classes.copyIcon} />
              </IconButton>
            )}
          </CopyToClipboard>
        </div>
        <RowDivider>
          <BaseTypography className={classes.dividerTitle}>OR</BaseTypography>
        </RowDivider>
        <FormControl fullWidth style={{ marginBottom: 0 }}>
          {EnableManyCompanies ? (
            <RecipientSelector
              recipientTypes={['client']}
              onRecipientSelected={(vals) => {
                const clientCompanyIds = vals.map((val) => ({
                  clientId: val.id,
                  companyId: val.companyId,
                }));
                setFieldValue('fields.recipientIds', clientCompanyIds);
              }}
              name="formRecipientIds"
            />
          ) : (
            <MultiCategorySelect
              multiple
              includeInternalUsers={false}
              includeClientUsers
              optionToExclude={disableUserIds}
              filterResultsLimit={0}
              values={selectedClientIds}
              id="clients-select"
              label="Select client(s)"
              onChange={handleRecipientChange}
              placeholder="Type to find clients"
              showPlaceholder
              disabled={
                values.fields.disabled || isLoadingForm || isLoadingResponses
              }
              showClientsInOtherCompanies
              helperText={errors.fields && errors.fields.recipientIds}
              error={!!(errors.fields && errors.fields.recipientIds)}
              useFullClientIDs={false}
            />
          )}
        </FormControl>
        <div className="mt-[14px] text-right">
          <Button
            variant="contained"
            onClick={selectAllClients}
            color="secondary"
          >
            Select all clients
          </Button>
        </div>
      </>
    );
  };

  return { validationScheme, FormRenderer };
};
